import { Box, Container, Link, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';

import { Logo } from '../brand/Logo';
import { caliberToSlug } from '../../utils/caliber';
import { SOCIAL_PROFILES } from '../../config/social';

const POPULAR_CALIBERS: string[] = [
  '9mm Luger',
  '5.56 NATO',
  '.223 Remington',
  '.308 Winchester',
  '.22 LR',
];

interface FooterColumn {
  heading: string;
  links: Array<{ label: string; to: string }>;
}

const COLUMNS: FooterColumn[] = [
  {
    heading: 'Browse',
    links: [
      // #107: dropped a confusing 'Search → /' (homepage) link; 'All listings'
      // already points to the actual listings/search page.
      { label: 'All listings', to: '/search' },
      { label: 'In stock', to: '/search?inStockOnly=true' },
      { label: 'Best price per round', to: '/search?sort=pricePerRound&sortDir=asc' },
    ],
  },
  {
    heading: 'Cartridges',
    links: POPULAR_CALIBERS.map((c) => ({
      label: c,
      // #105: SEO-friendly slugged caliber URL (redirects into filtered search).
      to: `/ammo/${caliberToSlug(c)}`,
    })),
  },
  {
    heading: 'Account',
    links: [
      { label: 'Log in', to: '/login' },
      { label: 'Create account', to: '/register' },
      { label: 'Dashboard', to: '/dashboard' },
      { label: 'Watchlist', to: '/dashboard/watchlist' },
    ],
  },
  {
    heading: 'Company',
    links: [
      // Temporarily hidden until we have enough traffic to pitch advertisers
      // (#103). Keep the route + feature — just don't surface the entry point.
      // { label: 'Advertise', to: '/register-advertiser' },
      { label: 'About', to: '/about' },
      { label: 'FAQ', to: '/faq' },
      { label: 'Round Recon vs. AmmoSeek', to: '/roundrecon-vs-ammoseek' },
      { label: 'User responsibility', to: '/responsibility' },
      { label: 'Privacy', to: '/privacy' },
      { label: 'Terms', to: '/terms' },
    ],
  },
];

/**
 * Site footer (spec §17). Marked as `<footer>` landmark with a label so screen
 * readers can jump to it. Styled to the tactical surface; all links are real
 * in-app routes.
 */
export function Footer() {
  const theme = useTheme();
  const t = theme.palette.tactical;
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      aria-label="Site footer"
      sx={{ mt: 'auto', bgcolor: t.bg, borderTop: `1px solid ${t.line}`, color: t.text }}
    >
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 4,
            gridTemplateColumns: { xs: '1fr', sm: '1.6fr 1fr 1fr', md: '1.8fr repeat(5, 1fr)' },
            pb: 4,
            borderBottom: `1px solid ${t.line}`,
          }}
        >
          <Box>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Logo size={22} />
              <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                Round Recon
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{ mt: 1.75, color: t.dim, lineHeight: 1.6, maxWidth: 320 }}
            >
              Independent ammunition price comparison. We&apos;re not a retailer — we point you at
              the best price and never process payments.
            </Typography>
          </Box>

          {COLUMNS.map((col) => (
            <Box key={col.heading} component="nav" aria-label={col.heading}>
              <Typography
                component="div"
                sx={{ fontSize: '0.8125rem', fontWeight: 600, color: t.text, mb: 1.75 }}
              >
                {col.heading}
              </Typography>
              <Stack spacing={1.1}>
                {col.links.map((l) => (
                  <Link
                    key={l.label}
                    component={RouterLink}
                    to={l.to}
                    underline="hover"
                    sx={{ fontSize: '0.8125rem', color: t.dim, '&:hover': { color: t.text } }}
                  >
                    {l.label}
                  </Link>
                ))}
              </Stack>
            </Box>
          ))}

          <Box component="nav" aria-label="Social media">
            <Typography
              component="div"
              sx={{ fontSize: '0.8125rem', fontWeight: 600, color: t.text, mb: 1.75 }}
            >
              Follow
            </Typography>
            <Stack spacing={1.1}>
              {SOCIAL_PROFILES.map((s) => (
                <Link
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  // rel="me" helps Mastodon/IndieWeb verify the profile owner.
                  rel="me noopener noreferrer"
                  underline="hover"
                  sx={{ fontSize: '0.8125rem', color: t.dim, '&:hover': { color: t.text } }}
                >
                  {s.label}
                </Link>
              ))}
            </Stack>
          </Box>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          sx={{ mt: 3, fontFamily: t.monoFont, fontSize: 11, color: t.dim }}
        >
          <span>© {year} Round Recon. Purchases occur on third-party retailer sites.</span>
          <span>We never ship · We never sell · Always verify on the retailer&apos;s site</span>
        </Stack>
      </Container>
    </Box>
  );
}

export default Footer;
